import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import AppShell from "../components/layout/AppShell";
import LeadDetailDrawer from "../components/leads/LeadDetailDrawer";
import { InboxAuditPromoEmptyState } from "../components/audit/InboxAuditPromoEmptyState.jsx";
import Card from "../components/ui/Card";
import { PageFirstLoadSpinner } from "../components/ui/PageFirstLoadSpinner.jsx";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import { useMailboxScope } from "../contexts/MailboxScopeContext";
import { leadsService } from "../services/leads";
import { brandProfileService } from "../services/brandProfile";
import { getScoreBadgeClass } from "../utils/scoreColors";

/** Match Button primary/secondary `sm` styles for `<Link>` / `<a>` actions. */
const QUEUE_LINK_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded tracking-wide uppercase text-xs transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-main bg-theme-accent text-[#07090C] border border-theme-accent font-mono hover:opacity-90 focus-visible:ring-theme-accent px-3 py-2";
const QUEUE_LINK_SECONDARY =
  "inline-flex items-center justify-center gap-2 rounded tracking-wide uppercase text-xs transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-main bg-theme-panel text-theme-text border border-theme-border font-mono hover:border-theme-border-bright focus-visible:ring-theme-accent px-3 py-2";

function formatQueueDate(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Prefer provider message time (`received_at`), then lead row time for older imports. */
function emailTimelineValue(lead) {
  return lead.received_at || lead.create_date || null;
}

function companyRoleLine(companyRaw, roleRaw) {
  const company = (companyRaw || "").trim();
  const role = (roleRaw || "").trim();
  if (company && role) return `${company} | ${role}`;
  return company || role || "";
}

export function LeadDetailOutlet() {
  const { leadHash } = useParams();
  const navigate = useNavigate();
  if (!leadHash) return null;
  return <LeadDetailDrawer leadHash={leadHash} onClose={() => navigate("/leads/")} />;
}

const Leads = ({ currentUser }) => {
  const queryClient = useQueryClient();
  const mailboxScope = useMailboxScope();
  const [brandFilter, setBrandFilter] = useState("");
  const [sortBy, setSortBy] = useState("match");

  const brandsQuery = useQuery({
    queryKey: ["brand-profiles-all"],
    queryFn: brandProfileService.listAll,
  });
  const brands = brandsQuery.data?.results ?? [];
  const showBrandSelect = brands.length > 1;

  useEffect(() => {
    if (showBrandSelect) return;
    setBrandFilter("");
  }, [showBrandSelect]);

  const queueQuery = useQuery({
    queryKey: ["leads", mailboxScope?.mailboxHash, brandFilter],
    queryFn: () =>
      leadsService.getQueue({
        mailbox: mailboxScope?.mailboxHash || "",
        brand: brandFilter,
      }),
    enabled: Boolean(mailboxScope?.mailboxHash) && !mailboxScope?.mailboxesLoading,
  });

  const dismissMutation = useMutation({
    mutationFn: (leadHash) => leadsService.dismiss(leadHash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
    },
  });

  const scopeBooting = Boolean(mailboxScope?.mailboxesLoading);
  const mailboxReady = Boolean(mailboxScope?.mailboxHash) && !scopeBooting;
  const showLeadsBodySpinner = scopeBooting || (mailboxReady && queueQuery.isLoading);
  const data = queueQuery.data;
  const hasListContent = mailboxReady && Boolean(data) && !data.empty;

  const sortedResults = useMemo(() => {
    const r = [...(data?.results || [])];
    if (sortBy === "recent") {
      r.sort(
        (a, b) =>
          new Date(emailTimelineValue(b) || 0) - new Date(emailTimelineValue(a) || 0)
      );
    } else {
      r.sort((a, b) => {
        const scoreB = b.match_score ?? 0;
        const scoreA = a.match_score ?? 0;
        if (scoreB !== scoreA) return scoreB - scoreA;
        return new Date(emailTimelineValue(b) || 0) - new Date(emailTimelineValue(a) || 0);
      });
    }
    return r;
  }, [data?.results, sortBy]);
  return (
    <AppShell
      currentUser={currentUser}
      pageTitle="Lead Queue"
      pageSubtitle={hasListContent ? `${data.queue_count} leads pending` : "High-fit outreach in one place"}
    >
      <div className="space-y-6">
        <div className="hidden md:flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">
              Lead Queue
            </h1>
            <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
              {hasListContent ? `${data.queue_count} leads pending action` : "Audit your inbox to populate the queue"}
            </p>
          </div>
          {hasListContent ? (
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Sort queue"
              className="min-w-[11rem] rounded-lg border border-theme-border bg-theme-main px-3 py-2 font-mono text-sm text-theme-text focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="match">Highest match</option>
              <option value="recent">Most recent</option>
            </select>
            {showBrandSelect ? (
              <select
                aria-label="Filter queue by brand"
                className="min-w-[12rem] rounded-lg border border-theme-border bg-theme-main px-3 py-2 font-mono text-sm text-theme-text focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
              >
                <option value="">All brands</option>
                {brands.map((b) => (
                  <option key={b.hash} value={b.hash}>
                    {b.company_name?.trim() || "Brand"}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          ) : null}
        </div>
        {hasListContent ? (
        <div className="flex md:hidden flex-row flex-wrap items-center gap-3">
          <select
            aria-label="Sort queue"
            className="min-w-0 flex-[1_1_8rem] rounded-lg border border-theme-border bg-theme-main px-3 py-2 font-mono text-sm text-theme-text focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="match">Highest match</option>
            <option value="recent">Most recent</option>
          </select>
          {showBrandSelect ? (
            <select
              aria-label="Filter queue by brand"
              className="min-w-0 flex-[1_1_8rem] rounded-lg border border-theme-border bg-theme-main px-3 py-2 font-mono text-sm text-theme-text focus:outline-none focus:ring-1 focus:ring-theme-accent/60"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <option value="">All brands</option>
              {brands.map((b) => (
                <option key={b.hash} value={b.hash}>
                  {b.company_name?.trim() || "Brand"}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        ) : null}

        {!mailboxScope?.mailboxHash && !scopeBooting ? (
          <Card className="p-8 text-center">
            <p className="text-theme-text">Connect a mailbox in Settings to see your queue.</p>
          </Card>
        ) : null}

        {showLeadsBodySpinner ? <PageFirstLoadSpinner /> : null}

        {!showLeadsBodySpinner && mailboxScope?.mailboxHash && data?.empty ? <InboxAuditPromoEmptyState /> : null}

        {!showLeadsBodySpinner && mailboxScope?.mailboxHash && data && !data.empty ? (
          <div className="space-y-3">
            {sortedResults.map((lead) => {
              const queuedLabel = formatQueueDate(emailTimelineValue(lead));
              const subtitle = companyRoleLine(lead.sender_company, lead.sender_role_title);
              const cardTitle = (lead.sender_name || "").trim() || (lead.sender_email || "").trim() || "Lead";
              return (
              <Card
                key={lead.hash}
                className="transition-all p-6"
              >
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display font-bold text-xl text-theme-on-background leading-snug truncate">
                        {cardTitle}
                      </h3>
                      {subtitle ? (
                        <p className="font-mono text-[11px] text-theme-dim mt-1 truncate" title={subtitle}>
                          {subtitle}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className="shrink-0 max-w-[45%] min-w-0 sm:max-w-[50%]"
                      title={`${(lead.brand_label || lead.brand_name || "Brand").trim()} - ${lead.match_score}% fit`}
                    >
                      <Badge
                        variant="inherit"
                        className={`${getScoreBadgeClass(lead.match_score)} max-w-full overflow-hidden text-ellipsis whitespace-nowrap`}
                      >
                        {(lead.brand_label || lead.brand_name || "Brand").trim()} - {lead.match_score}% fit
                      </Badge>
                    </span>
                  </div>
                  {lead.match_reason ? (
                    <p className="font-mono text-[12px] text-theme-muted leading-relaxed">{lead.match_reason}</p>
                  ) : null}
                </div>
                {(lead.reply_email?.body || "").trim() ? (
                  <div className="mt-4 p-4 bg-theme-main border border-theme-border rounded-lg">
                    <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim mb-2">
                      Flip pitch preview
                    </p>
                    <p className="text-theme-text text-sm italic">{(lead.reply_email.body || "").trim()}</p>
                  </div>
                ) : null}
                <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/leads/${lead.hash}/`} className={QUEUE_LINK_PRIMARY}>
                      Review &amp; Reply
                    </Link>
                    {lead.inbox_url ? (
                      <a
                        href={lead.inbox_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={QUEUE_LINK_SECONDARY}
                      >
                        View in Gmail
                      </a>
                    ) : (
                      <span className={`${QUEUE_LINK_SECONDARY} opacity-50 pointer-events-none`} aria-disabled="true">
                        View in Gmail
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="inline-flex items-center gap-1"
                      onClick={() => dismissMutation.mutate(lead.hash)}
                    >
                      <span className="material-symbols-outlined text-[16px] leading-none">thumb_down</span>
                      Dismiss
                    </Button>
                  </div>
                  {queuedLabel ? (
                    <div className="ml-auto flex min-w-0 max-w-full items-center gap-1 font-mono text-[11px] text-theme-dim shrink-0">
                      <span className="material-symbols-outlined shrink-0 text-[14px] leading-none" aria-hidden>
                        schedule
                      </span>
                      <span className="truncate">{queuedLabel}</span>
                    </div>
                  ) : null}
                </div>
              </Card>
              );
            })}
          </div>
        ) : null}

        <Outlet />
      </div>
    </AppShell>
  );
};

export default Leads;
